/*
 * Molten Store homepage V14.3 interactions.
 * Homepage-only, editor-safe, and framework independent.
 */

(() => {
  const desktopQuery = window.matchMedia("(min-width: 1200px)");
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  class MoltenHomeHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;

      this.button = this.querySelector("[data-mhp143-menu-toggle]");
      this.menu = this.querySelector("[data-mhp143-mobile-menu]");
      this.links = Array.from(this.menu?.querySelectorAll("a") ?? []);

      if (!this.button || !this.menu) return;

      this.handleButtonClick = () => {
        this.setMenu(this.button.getAttribute("aria-expanded") !== "true");
      };
      this.handleKeydown = (event) => {
        if (event.key === "Escape" && this.button.getAttribute("aria-expanded") === "true") {
          this.setMenu(false, true);
        }
      };
      this.handleDesktopChange = (event) => {
        if (event.matches) this.setMenu(false);
      };
      this.handleLinkClick = () => this.setMenu(false);

      this.button.addEventListener("click", this.handleButtonClick);
      document.addEventListener("keydown", this.handleKeydown);
      desktopQuery.addEventListener?.("change", this.handleDesktopChange);
      this.links.forEach((link) => link.addEventListener("click", this.handleLinkClick));

      this.menu.inert = true;
      this.dataset.initialized = "true";
    }

    disconnectedCallback() {
      if (!this.button || !this.menu) return;

      this.button.removeEventListener("click", this.handleButtonClick);
      document.removeEventListener("keydown", this.handleKeydown);
      desktopQuery.removeEventListener?.("change", this.handleDesktopChange);
      this.links.forEach((link) => link.removeEventListener("click", this.handleLinkClick));
      window.clearTimeout(this.focusTimer);
      document.body.classList.remove("menu-open");
      delete this.dataset.initialized;
    }

    setMenu(open, returnFocus = false) {
      const openLabel = this.button.dataset.openLabel || "Menu";
      const closeLabel = this.button.dataset.closeLabel || "Close";

      document.body.classList.toggle("menu-open", open);
      this.button.setAttribute("aria-expanded", String(open));
      this.button.textContent = open ? closeLabel : openLabel;
      this.menu.setAttribute("aria-hidden", String(!open));
      this.menu.inert = !open;
      window.clearTimeout(this.focusTimer);

      if (open) {
        this.focusTimer = window.setTimeout(() => this.menu.querySelector("a")?.focus(), 100);
      } else if (returnFocus) {
        this.button.focus();
      }
    }
  }

  if (!customElements.get("molten-home-header")) {
    customElements.define("molten-home-header", MoltenHomeHeader);
  }

  const initSkipLink = () => {
    const skipLink = document.querySelector("[data-mhp143-skip-link]");
    const main = document.getElementById("main-content");

    if (!skipLink || !main || skipLink.dataset.initialized === "true") return;

    skipLink.addEventListener("click", (event) => {
      event.preventDefault();
      history.replaceState(null, "", "#main-content");
      main.focus({ preventScroll: true });
      main.scrollIntoView({
        block: "start",
        behavior: reducedMotionQuery.matches ? "auto" : "smooth"
      });
    });

    skipLink.dataset.initialized = "true";
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initSkipLink, { once: true });
  } else {
    initSkipLink();
  }

  document.addEventListener("shopify:section:load", initSkipLink);
})();
